import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BookOpen, Eye } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob } from "@/components/app/ImportDialog";
import { PageHeader } from "@/components/app/bits";
import { ComponentsMap } from "@/components/app/learn/LearnSections";

export default function Learn() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [example, setExample] = useState(null);
  useEffect(() => { api.get("/learn").then((r) => setData(r.data)); api.get("/example").then((r) => setExample(r.data)); }, []);
  if (!data) return <div className="text-muted-foreground text-sm">Loading…</div>;
  const centre = data.articles.find((a) => a.slug === "missing-components");
  const changeId = params.get("change");
  return (
    <div data-testid="learn-page">
      <PageHeader overline="Learn" title="How to actually do this" sub="The toolkit behind the buttons — what to look for, what to say, and what to do when a number is bad. Short on purpose." />
      <div className="grid lg:grid-cols-[1.2fr_.8fr] gap-5">
        <div className="pp-card p-6" data-testid="learn-centrepiece">
          <div className="pp-overline">Start here</div>
          <h2 className="font-head text-2xl font-extrabold tracking-tight mt-1">{centre.title}</h2>
          <p className="text-sm text-subtle mt-2">{centre.opener}</p>
          <div className="mt-4"><ComponentsMap components={data.components} tabs={data.tabs} changeId={changeId} compact /></div>
          <Link to={`/app/learn/${centre.slug}${changeId ? `?change=${changeId}` : ""}`} data-testid="learn-open-missing-components" className="inline-flex items-center gap-1.5 text-sm font-bold text-accent-700 hover:text-accent-900 mt-4">Read the full map <ArrowRight size={14} /></Link>
        </div>
        <div className="space-y-5">
          {example?.change_id && (
            <Link to={`/app/changes/${example.change_id}`} data-testid="learn-open-example" className="block pp-card pp-card-hover p-6 bg-ink text-on-dark border-ink">
              <div className="flex items-center gap-2 text-brand-300 text-xs font-bold uppercase tracking-widest"><Eye size={14} /> Worked example · read-only</div>
              <div className="font-head text-xl font-extrabold mt-2">What good looks like</div>
              <p className="text-sm text-faint mt-2 leading-relaxed">One change run start to finish: case, sponsor, seven stakeholders across all five types, a readiness dip and its recovery, comms delivered, numbers that agreed in the end. Including the bit that went wrong.</p>
              <div className="inline-flex items-center gap-1.5 text-sm font-bold mt-4">Open {example.ref} <ArrowRight size={14} /></div>
            </Link>
          )}
          <div className="pp-card p-5 text-sm text-subtle">
            <div className="flex items-center gap-2 font-bold text-foreground"><BookOpen size={15} /> Source</div>
            <p className="mt-1.5 leading-relaxed"><button data-testid="template-download-learn" onClick={() => downloadBlob("/templates/change-workbook", "ChangeOS-change-template.xlsx")} className="font-bold text-accent-700 hover:text-accent-900">Download the blank change workbook</button> — every toolkit template as a spreadsheet with dropdowns and one example row per sheet, importable as a new change.</p>
            <p className="mt-1.5 leading-relaxed">Everything here is transcribed from the Acclaim Change Management Toolkit, with ERP-specific wording generalised to "the change". Where the app links a consequence to a tab, that is the toolkit's own remedy, placed where you'd use it.</p>
          </div>
        </div>
      </div>
      {data.groups.map((g) => (
        <section key={g} className="mt-10" data-testid={`learn-group-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <h2 className="font-head text-xl font-extrabold tracking-tight">{g}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {data.articles.filter((a) => a.group === g).map((a) => (
              <Link key={a.slug} to={`/app/learn/${a.slug}`} data-testid={`learn-card-${a.slug}`} className="pp-card pp-card-hover p-5 block">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span>{a.minutes} min</span>{a.featured && <span className="pp-pill py-0 text-[10px]">Most useful</span>}</div>
                <div className="font-head text-lg font-extrabold mt-2 leading-snug">{a.title}</div>
                <p className="text-sm text-subtle mt-2 leading-relaxed">{a.opener}</p>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
